// PodCropper — fullscreen modal that lets the guard adjust 4 corner handles
// over their captured photo, then warps the quadrilateral into a clean
// rectangle (perspective-corrected POD). Returns the cropped image as a Blob.
//
// Interaction: tap-and-drag any of the 4 corner dots. Pinch-zoom is disabled
// to avoid fighting gestures. "Use original" skips cropping entirely.
//
// The warp is a bilinear sampler along the quadrilateral edges — simpler than
// a full 3x3 homography but visually indistinguishable for document-sized
// rectangles, which is the only case we care about.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useEscapeKey } from "../hooks/useEscapeKey";

const HANDLE_R = 14;   // visible radius (px in display space)
const HIT_R = 28;      // touch target radius
const MAX_OUT = 1800;  // max output image edge

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

// Warp source image quadrilateral into a rectangle via bilinear edge sampling.
function warpQuadToRect(srcCanvas, corners /* {tl,tr,br,bl} in src px */) {
  const { tl, tr, br, bl } = corners;
  const widthTop = Math.hypot(tr.x - tl.x, tr.y - tl.y);
  const widthBot = Math.hypot(br.x - bl.x, br.y - bl.y);
  const heightL = Math.hypot(bl.x - tl.x, bl.y - tl.y);
  const heightR = Math.hypot(br.x - tr.x, br.y - tr.y);
  let outW = Math.round(Math.max(widthTop, widthBot));
  let outH = Math.round(Math.max(heightL, heightR));
  const scale = Math.min(1, MAX_OUT / Math.max(outW, outH));
  outW = Math.max(1, Math.round(outW * scale));
  outH = Math.max(1, Math.round(outH * scale));

  const out = document.createElement("canvas");
  out.width = outW;
  out.height = outH;
  const octx = out.getContext("2d");

  const srcCtx = srcCanvas.getContext("2d");
  const srcW = srcCanvas.width;
  const srcH = srcCanvas.height;
  const srcData = srcCtx.getImageData(0, 0, srcW, srcH).data;
  const outImg = octx.createImageData(outW, outH);
  const outData = outImg.data;

  // Bilinear sample from src at floating-point (x,y).
  function sample(x, y) {
    if (x < 0 || x >= srcW - 1 || y < 0 || y >= srcH - 1) return [0, 0, 0, 255];
    const x0 = Math.floor(x), y0 = Math.floor(y);
    const dx = x - x0, dy = y - y0;
    const i00 = (y0 * srcW + x0) * 4;
    const i10 = i00 + 4;
    const i01 = i00 + srcW * 4;
    const i11 = i01 + 4;
    const w00 = (1 - dx) * (1 - dy);
    const w10 = dx * (1 - dy);
    const w01 = (1 - dx) * dy;
    const w11 = dx * dy;
    return [
      srcData[i00] * w00 + srcData[i10] * w10 + srcData[i01] * w01 + srcData[i11] * w11,
      srcData[i00 + 1] * w00 + srcData[i10 + 1] * w10 + srcData[i01 + 1] * w01 + srcData[i11 + 1] * w11,
      srcData[i00 + 2] * w00 + srcData[i10 + 2] * w10 + srcData[i01 + 2] * w01 + srcData[i11 + 2] * w11,
      255,
    ];
  }

  for (let y = 0; y < outH; y++) {
    const v = y / (outH - 1 || 1);
    for (let x = 0; x < outW; x++) {
      const u = x / (outW - 1 || 1);
      // Point on top edge
      const tx = tl.x + u * (tr.x - tl.x);
      const ty = tl.y + u * (tr.y - tl.y);
      // Point on bottom edge
      const bx = bl.x + u * (br.x - bl.x);
      const by = bl.y + u * (br.y - bl.y);
      // Interp down
      const sx = tx + v * (bx - tx);
      const sy = ty + v * (by - ty);
      const [r, g, b, a] = sample(sx, sy);
      const i = (y * outW + x) * 4;
      outData[i] = r;
      outData[i + 1] = g;
      outData[i + 2] = b;
      outData[i + 3] = a;
    }
  }
  octx.putImageData(outImg, 0, 0);
  return out;
}

function canvasToBlob(canvas, type = "image/jpeg", quality = 0.88) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

// ── Auto-detect the document ───────────────────────────────────────────
// Seeds the corner handles on the paper's corners so the guard usually
// just taps Save. Pure heuristic, no dependencies:
//   1. Downscale to ~240px, grayscale.
//   2. Otsu threshold — paper (bright) vs background.
//   3. Largest 4-connected bright component.
//   4. Corners = that component's extreme pixels along the two diagonals
//      (argmin/argmax of x+y and x−y) — matches the warp's quad model,
//      so a tilted form gets a perspective-correct seed.
// Returns {tl,tr,br,bl} in SOURCE pixels, or null when the guess looks
// unreliable — caller falls back to the old 5%-inset seed and the guard
// drags as before.
function detectDocumentCorners(srcCanvas) {
  try {
    const maxEdge = 240;
    const scale = Math.min(1, maxEdge / Math.max(srcCanvas.width, srcCanvas.height));
    const w = Math.max(1, Math.round(srcCanvas.width * scale));
    const h = Math.max(1, Math.round(srcCanvas.height * scale));
    const small = document.createElement("canvas");
    small.width = w;
    small.height = h;
    const ctx = small.getContext("2d");
    ctx.drawImage(srcCanvas, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    const n = w * h;
    const gray = new Uint8Array(n);
    const hist = new Uint32Array(256);
    for (let i = 0; i < n; i++) {
      const g = ((data[i * 4] * 299 + data[i * 4 + 1] * 587 + data[i * 4 + 2] * 114) / 1000) | 0;
      gray[i] = g;
      hist[g]++;
    }
    // Otsu: threshold that maximizes between-class variance.
    let sum = 0;
    for (let t = 0; t < 256; t++) sum += t * hist[t];
    let sumB = 0, wB = 0, thr = 127, bestVar = -1;
    for (let t = 0; t < 256; t++) {
      wB += hist[t];
      if (wB === 0) continue;
      const wF = n - wB;
      if (wF === 0) break;
      sumB += t * hist[t];
      const mB = sumB / wB;
      const mF = (sum - sumB) / wF;
      const v = wB * wF * (mB - mF) * (mB - mF);
      if (v > bestVar) { bestVar = v; thr = t; }
    }
    const bright = new Uint8Array(n);
    for (let i = 0; i < n; i++) bright[i] = gray[i] > thr ? 1 : 0;
    // Largest connected bright component (BFS, 4-neighbour).
    const label = new Int32Array(n).fill(-1);
    const queue = new Int32Array(n);
    let bestId = -1, bestSize = 0, id = 0;
    for (let i = 0; i < n; i++) {
      if (!bright[i] || label[i] !== -1) continue;
      label[i] = id;
      queue[0] = i;
      let qh = 0, qt = 1, size = 0;
      while (qh < qt) {
        const p = queue[qh++];
        size++;
        const px = p % w;
        const py = (p / w) | 0;
        if (px > 0 && bright[p - 1] && label[p - 1] === -1) { label[p - 1] = id; queue[qt++] = p - 1; }
        if (px < w - 1 && bright[p + 1] && label[p + 1] === -1) { label[p + 1] = id; queue[qt++] = p + 1; }
        if (py > 0 && bright[p - w] && label[p - w] === -1) { label[p - w] = id; queue[qt++] = p - w; }
        if (py < h - 1 && bright[p + w] && label[p + w] === -1) { label[p + w] = id; queue[qt++] = p + w; }
      }
      if (size > bestSize) { bestSize = size; bestId = id; }
      id++;
    }
    // Too small a region = probably not the document; let the guard drag.
    if (bestId < 0 || bestSize < n * 0.12) return null;
    let tl = null, tr = null, br = null, bl = null;
    let minSum = Infinity, maxSum = -Infinity, minDiff = Infinity, maxDiff = -Infinity;
    for (let i = 0; i < n; i++) {
      if (label[i] !== bestId) continue;
      const x = i % w;
      const y = (i / w) | 0;
      const s = x + y;
      const dif = x - y;
      if (s < minSum) { minSum = s; tl = { x, y }; }
      if (s > maxSum) { maxSum = s; br = { x, y }; }
      if (dif > maxDiff) { maxDiff = dif; tr = { x, y }; }
      if (dif < minDiff) { minDiff = dif; bl = { x, y }; }
    }
    if (!tl || !tr || !br || !bl) return null;
    // Sanity: quad must cover a meaningful chunk of the frame.
    const quadArea =
      Math.abs((tr.x - tl.x) * (bl.y - tl.y) - (bl.x - tl.x) * (tr.y - tl.y)) / 2 +
      Math.abs((br.x - tr.x) * (bl.y - tr.y) - (bl.x - tr.x) * (br.y - tr.y)) / 2;
    if (quadArea < n * 0.1) return null;
    // Back to source pixels, nudged slightly outward so tight detections
    // don't shave the paper's edge.
    const inv = 1 / scale;
    const pad = 0.01 * Math.max(srcCanvas.width, srcCanvas.height);
    const clampP = (x, y) => ({
      x: Math.max(0, Math.min(srcCanvas.width, x)),
      y: Math.max(0, Math.min(srcCanvas.height, y)),
    });
    return {
      tl: clampP(tl.x * inv - pad, tl.y * inv - pad),
      tr: clampP(tr.x * inv + pad, tr.y * inv - pad),
      br: clampP(br.x * inv + pad, br.y * inv + pad),
      bl: clampP(bl.x * inv - pad, bl.y * inv + pad),
    };
  } catch {
    return null;
  }
}

export default function PodCropper({ file, onDone, onCancel, onSkip }) {
  useEscapeKey(() => onCancel?.());
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const srcCanvasRef = useRef(null); // full-res copy of the source image
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [displayScale, setDisplayScale] = useState(1);
  // corners in SOURCE pixel coords. Default = full image rect.
  const [corners, setCorners] = useState(null);
  const [dragging, setDragging] = useState(null); // key: tl|tr|br|bl
  const [saving, setSaving] = useState(false);
  // Has the user rotated? "Use original" hands back the untouched file, which
  // silently discards a rotation — the one edit most likely to be the WHOLE
  // reason someone opened this. Track it so that button exports the rotated
  // image instead when there's something to keep.
  const [rotatedOnce, setRotatedOnce] = useState(false);
  // True when the corner seed came from detectDocumentCorners — drives
  // the header copy ("check the auto-crop" vs "drag the corners").
  const [autoDetected, setAutoDetected] = useState(false);

  // Load file → full-res offscreen canvas.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Try createImageBitmap FIRST — it can decode formats (incl. HEIC on
      // newer Safari) that <img> + URL.createObjectURL can't always handle.
      // Fall back to <img> only if bitmap path fails.
      let bitmap = null;
      let imgFallback = null;
      try {
        bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      } catch {
        try { bitmap = await createImageBitmap(file); } catch { /* try img next */ }
      }
      if (!bitmap) {
        try { imgFallback = await loadImageFromFile(file); } catch (err) {
          // Unsupported format or corrupt file — bail to upload-as-is so the
          // guard doesn't lose their photo. The original file is still valid
          // for storage even if we can't render it for cropping.
          if (cancelled) return;
          console.warn("PodCropper couldn't decode — skipping crop, uploading original", err);
          onSkip?.(file);
          return;
        }
      }
      if (cancelled) return;
      const source = bitmap || imgFallback;
      const w = source.width || source.naturalWidth;
      const h = source.height || source.naturalHeight;
      if (!w || !h) {
        console.warn("PodCropper got 0-dim image — skipping crop");
        onSkip?.(file);
        return;
      }
      const src = document.createElement("canvas");
      src.width = w;
      src.height = h;
      src.getContext("2d").drawImage(source, 0, 0, w, h);
      srcCanvasRef.current = src;
      setImgSize({ w, h });
      // Auto-crop: seed the handles on the detected paper so the guard
      // usually just approves. Unreliable detection → old 5% inset.
      const detected = detectDocumentCorners(src);
      if (detected) {
        setCorners(detected);
        setAutoDetected(true);
      } else {
        const ix = w * 0.05, iy = h * 0.05;
        setCorners({
          tl: { x: ix, y: iy },
          tr: { x: w - ix, y: iy },
          br: { x: w - ix, y: h - iy },
          bl: { x: ix, y: h - iy },
        });
        setAutoDetected(false);
      }
    })();
    return () => { cancelled = true; };
    // Only re-run if the file itself changes — `onCancel`/`onSkip` callbacks
    // are recreated on every parent render and would loop the effect otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // Compute display scale so the WHOLE image fits the viewport (always
  // zoomed out to fit — pinch-zoom is disabled, so a too-large scale would
  // strand the guard zoomed in). A plain on-load measure can read the
  // container as 0 before the modal finishes laying out and fall back to
  // full resolution; a ResizeObserver re-fits the moment it has a real
  // size and on every later resize.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !imgSize.w) return;
    function compute() {
      const avail = el.getBoundingClientRect();
      if (!avail.width || !avail.height) return; // not laid out yet — RO fires again
      const s = Math.min(avail.width / imgSize.w, avail.height / imgSize.h);
      if (s > 0 && Number.isFinite(s)) setDisplayScale(s);
    }
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("resize", compute);
    return () => { ro.disconnect(); window.removeEventListener("resize", compute); };
  }, [imgSize]);

  // Render loop — draw image + overlay + handles.
  useEffect(() => {
    const canvas = canvasRef.current;
    const src = srcCanvasRef.current;
    if (!canvas || !src || !corners) return;
    const dw = Math.round(imgSize.w * displayScale);
    const dh = Math.round(imgSize.h * displayScale);
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(src, 0, 0, dw, dh);

    // Dim outside the quadrilateral.
    const toDisp = (p) => ({ x: p.x * displayScale, y: p.y * displayScale });
    const d = { tl: toDisp(corners.tl), tr: toDisp(corners.tr), br: toDisp(corners.br), bl: toDisp(corners.bl) };
    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.beginPath();
    ctx.rect(0, 0, dw, dh);
    ctx.moveTo(d.tl.x, d.tl.y);
    ctx.lineTo(d.bl.x, d.bl.y);
    ctx.lineTo(d.br.x, d.br.y);
    ctx.lineTo(d.tr.x, d.tr.y);
    ctx.closePath();
    ctx.fill("evenodd");
    ctx.restore();

    // Quadrilateral outline.
    ctx.strokeStyle = "#22c55e";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(d.tl.x, d.tl.y);
    ctx.lineTo(d.tr.x, d.tr.y);
    ctx.lineTo(d.br.x, d.br.y);
    ctx.lineTo(d.bl.x, d.bl.y);
    ctx.closePath();
    ctx.stroke();

    // Corner handles.
    for (const k of ["tl", "tr", "br", "bl"]) {
      const p = d[k];
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.arc(p.x, p.y, HANDLE_R, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = 3;
      ctx.stroke();
    }
  }, [corners, displayScale, imgSize]);

  // Pointer handlers.
  function getPointerPos(e) {
    const canvas = canvasRef.current;
    const r = canvas.getBoundingClientRect();
    const x = (e.clientX - r.left);
    const y = (e.clientY - r.top);
    // Convert display px → source px
    return { x: x / displayScale, y: y / displayScale };
  }

  function handlePointerDown(e) {
    if (!corners) return;
    const p = getPointerPos(e);
    let best = null, bestD = Infinity;
    for (const k of ["tl", "tr", "br", "bl"]) {
      const dx = (corners[k].x - p.x) * displayScale;
      const dy = (corners[k].y - p.y) * displayScale;
      const d = Math.hypot(dx, dy);
      if (d < bestD) { bestD = d; best = k; }
    }
    if (bestD <= HIT_R) {
      setDragging(best);
      e.preventDefault();
    }
  }

  function handlePointerMove(e) {
    if (!dragging) return;
    const p = getPointerPos(e);
    const clamped = {
      x: Math.max(0, Math.min(imgSize.w, p.x)),
      y: Math.max(0, Math.min(imgSize.h, p.y)),
    };
    setCorners((c) => ({ ...c, [dragging]: clamped }));
    e.preventDefault();
  }

  function handlePointerUp() {
    setDragging(null);
  }

  // Rotate the source canvas 90° clockwise. Replaces srcCanvasRef in place,
  // swaps imgSize dimensions, and re-seats the corners to a 5% inset on the
  // new canvas. Everything downstream (display, drag, warp) keeps working
  // because they all operate in source-pixel space.
  function rotate90CW() {
    const src = srcCanvasRef.current;
    if (!src) return;
    setRotatedOnce(true);
    const out = document.createElement("canvas");
    out.width = src.height;
    out.height = src.width;
    const ctx = out.getContext("2d");
    // Translate to the new canvas's "top-right corner from the source's
    // perspective", then rotate 90° clockwise. Result: source pixel (x,y)
    // ends up at output pixel (h - y, x).
    ctx.translate(out.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(src, 0, 0);
    srcCanvasRef.current = out;
    setImgSize({ w: out.width, h: out.height });
    // Re-run auto-detection on the rotated canvas; fall back to the inset.
    const detected = detectDocumentCorners(out);
    if (detected) {
      setCorners(detected);
      setAutoDetected(true);
    } else {
      const ix = out.width * 0.05, iy = out.height * 0.05;
      setCorners({
        tl: { x: ix, y: iy },
        tr: { x: out.width - ix, y: iy },
        br: { x: out.width - ix, y: out.height - iy },
        bl: { x: ix, y: out.height - iy },
      });
      setAutoDetected(false);
    }
  }

  // "Use whole photo" — skip the crop but KEEP any rotation. With no
  // rotation we hand back the original file untouched, which avoids a
  // pointless re-encode and keeps the previous behaviour byte-for-byte.
  async function handleUseWhole() {
    if (!rotatedOnce || !srcCanvasRef.current) { onSkip?.(file); return; }
    setSaving(true);
    try {
      const blob = await canvasToBlob(srcCanvasRef.current);
      if (!blob) throw new Error("Export failed");
      onSkip?.(new File([blob], (file.name || "pod") + "-rotated.jpg", { type: "image/jpeg" }));
    } catch (err) {
      console.error("Rotate-only export failed", err);
      onSkip?.(file);
    }
    setSaving(false);
  }

  async function handleConfirm() {
    if (!srcCanvasRef.current || !corners) return;
    setSaving(true);
    try {
      const out = warpQuadToRect(srcCanvasRef.current, corners);
      const blob = await canvasToBlob(out);
      if (!blob) throw new Error("Crop failed");
      // Wrap blob back into a File so the upload path is unchanged.
      const cropped = new File([blob], (file.name || "pod") + "-cropped.jpg", { type: "image/jpeg" });
      onDone(cropped);
    } catch (err) {
      console.error(err);
      alert("Couldn't crop — using original photo.");
      onSkip?.(file);
    }
    setSaving(false);
  }

  // Render through a portal to document.body. Without this, position:fixed
  // gets trapped inside the guard layout's stacking context (the bottom tab
  // bar paints over our buttons even at z-index 500). The portal escapes it.
  return createPortal((
    <div style={{
      position: "fixed", inset: 0, background: "#111", zIndex: 9999,
      // dvh = dynamic viewport height. Excludes mobile browser chrome unlike vh,
      // so the bottom action bar always stays visible.
      height: "100dvh",
      maxHeight: "100dvh",
      display: "flex", flexDirection: "column",
    }}>
      <div style={{
        padding: "12px 16px",
        // Add safe-area top padding so the title isn't behind the iOS notch.
        paddingTop: "calc(12px + env(safe-area-inset-top))",
        color: "#fff", fontSize: 14,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "rgba(0,0,0,0.6)",
        flexShrink: 0,
      }}>
        <div style={{ fontWeight: 600 }}>
          {autoDetected ? "Auto-cropped — adjust corners if needed" : "Drag corners to the paper edges"}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={rotate90CW}
            title="Rotate 90° clockwise"
            style={{ background: "transparent", color: "#fff", border: "1px solid #555", borderRadius: 6, padding: "6px 10px", fontSize: 14 }}
          >
            ↻
          </button>
          <button
            onClick={handleUseWhole}
            disabled={saving}
            title={rotatedOnce ? "Keep the rotation, skip the crop" : "Use the photo exactly as taken"}
            style={{ background: "transparent", color: "#fff", border: "1px solid #555", borderRadius: 6, padding: "6px 10px", fontSize: 12 }}
          >
            {rotatedOnce ? "Use rotated" : "Use original"}
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        style={{
          flex: 1, minHeight: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden", touchAction: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ maxWidth: "100%", maxHeight: "100%", touchAction: "none", cursor: dragging ? "grabbing" : "crosshair" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
      </div>

      <div style={{
        padding: "12px 16px",
        // Safe-area bottom padding so the buttons clear the iOS home indicator.
        paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
        display: "flex", gap: 10, justifyContent: "space-between",
        background: "rgba(0,0,0,0.85)",
        flexShrink: 0,
      }}>
        <button
          onClick={onCancel}
          style={{ flex: 1, padding: "14px", background: "transparent", color: "#fff", border: "1px solid #555", borderRadius: 8, fontSize: 14 }}
        >
          Retake
        </button>
        <button
          onClick={handleConfirm}
          disabled={saving || !corners}
          style={{
            flex: 2, padding: "14px",
            background: saving || !corners ? "#1d8b46" : "#22c55e",
            color: "#fff", border: "none", borderRadius: 8,
            fontSize: 16, fontWeight: 700,
            boxShadow: "0 -2px 12px rgba(34,197,94,0.35)",
          }}
        >
          {saving ? "Processing..." : "✓ Save crop"}
        </button>
      </div>
    </div>
  ), document.body);
}
